// ReadingDock — the trace reading while it is being made.
//
// The reading agent fans several judges over disjoint stretches of the axis at
// once, and each returns its verdicts as one streamed document. The axis strip
// carries the spatial half of that (a sweep per judge, a mark per verdict);
// this dock carries the half that needs words: where each judge is, what it is
// doing right now, and what it just finished reading.
//
// It is the same shell as every other dock — LanesDock's box, header row and
// body scroll, in the same slot below the plane — because it is one more detail
// expansion, not a surface of its own. Only the rows inside differ.
//
// It OUTLIVES the run, because the reading is the reader's answer and not a
// progress indicator. The run's rows and the settled report's rows are the same
// shape, so when the run lands the dock keeps rendering — from the REPORT,
// which is what the strip reads, so the two can never disagree — until the
// reader closes it.
//
// Two rules this surface exists to keep visible:
//   * banked work is named as banked. A ledger hit costs nothing and answers
//     instantly; letting it read as work this run did would overstate the run.
//   * a verdict shown here has already passed the gates the settled report
//     applies, and a failed batch takes its verdicts back off the screen.

import { memo, useState } from "react";

// The dock's own type scale, mirrored rather than imported: TraceLanesView
// keeps these module-private, and a shared export would be a wider change than
// this surface earns.
const META = "text-[11px] leading-[1.3]";
const EYEBROW_BASE = "text-[10px] uppercase tracking-[0.07em] leading-[1.2]";
const EYEBROW = `${EYEBROW_BASE} text-[color:var(--eyebrow)]`;
const OK = "var(--ok, #7fc9a3)";
const AMBER = "var(--amber, #b8860b)";

// The model's own writing, as one line. A preview that wrapped would resize the
// rows from under the reader every few tokens.
function oneLine(text, max = 120) {
  const flat = String(text || "").replace(/\s+/g, " ").trim();
  return flat.length > max ? `…${flat.slice(-max)}` : flat;
}

function turnRange(from, to) {
  // Turn numbers are 1-based everywhere the operator reads them.
  return from === to ? `Turn ${from + 1}` : `Turns ${from + 1}-${to + 1}`;
}

function SectionRow({ label, detail }) {
  return (
    <div className={`flex min-w-0 items-baseline gap-[8px] border-b border-[color:var(--line-soft)] px-[9px] py-[4px] ${EYEBROW}`}>
      <span className="shrink-0 whitespace-nowrap">{label}</span>
      {detail ? (
        <span className={`${META} min-w-0 flex-1 truncate normal-case tracking-normal text-[color:var(--muted)]`}>{detail}</span>
      ) : null}
    </div>
  );
}

// One judge, on its own stretch of the axis. The activity is the child's live
// state: reasoning before it writes, then the answer itself streaming out.
function HeadRow({ batch }) {
  const done = batch.status !== "reading";
  return (
    <div data-reading-head={batch.index} className="flex min-w-0 items-baseline gap-[9px] px-[9px] py-[2px]">
      <span className={`${EYEBROW} w-[104px] shrink-0 truncate`}>{turnRange(batch.from, batch.to)}</span>
      <span className={`${META} w-[56px] shrink-0 text-[color:var(--muted-soft)] tabular-nums`}>
        {batch.size} call{batch.size === 1 ? "" : "s"}
      </span>
      <span
        className={`${META} w-[124px] shrink-0 truncate`}
        style={{ color: done && batch.status === "error" ? "var(--red, #c0392b)" : undefined }}
      >
        <span className={done ? "" : "text-[color:var(--ink)]"}>
          {done ? (batch.status === "error" ? "failed" : "done") : batch.activity}
        </span>
      </span>
      <span className={`${META} min-w-0 flex-1 truncate font-mono text-[color:var(--muted-soft)]`}>
        {done ? "" : oneLine(batch.preview)}
      </span>
    </div>
  );
}

// One drafted claim as the judge ruled on it. The verdict schema asks for a
// reason whenever the judge REJECTS ("why, in <=15 words"), and the reading has
// always captured it -- showing it is the difference between "1 disputed" and
// knowing what the dispute was.
function ClaimLine({ claim }) {
  const tone = claim.verdict === "accept" ? OK : claim.verdict === "reject" ? AMBER : null;
  const word = claim.verdict === "accept" ? "kept" : claim.verdict === "reject" ? "disputed" : "unruled";
  return (
    <div data-reading-claim={claim.index} className="flex min-w-0 items-baseline gap-[9px]">
      <span className={`${META} w-[70px] shrink-0 tabular-nums`} style={tone ? { color: tone } : undefined}>
        <span className={tone ? "" : "text-[color:var(--muted-soft)]"}>{word}</span>
      </span>
      <span className={`${META} min-w-0 flex-1 truncate`}>
        <span className="text-[color:var(--muted-soft)]">{claim.domain}/{claim.kind} </span>
        <span className="font-mono text-[color:var(--ink)]">{claim.subject}</span>
        {claim.detail ? <span className="text-[color:var(--muted-soft)]"> — {oneLine(claim.detail, 70)}</span> : null}
        {claim.promoted ? <span className="text-[color:var(--muted-soft)]"> · from the last read</span> : null}
      </span>
      {claim.reason ? (
        <span className={`${META} w-[38%] shrink-0 truncate`} style={{ color: AMBER }}>{claim.reason}</span>
      ) : null}
    </div>
  );
}

// The work the drafters missed, each with the bytes that anchor it — an
// addition without its quote is exactly what this loop refuses to render.
function AdditionLine({ item }) {
  return (
    <div className="flex min-w-0 items-baseline gap-[9px]">
      <span className={`${META} w-[70px] shrink-0`} style={{ color: OK }}>missed</span>
      <span className={`${META} min-w-0 flex-1 truncate`}>
        <span className="text-[color:var(--ink)]">{item.kind} </span>
        <span className="font-mono text-[color:var(--muted)]">{item.subject}</span>
      </span>
      <span className={`${META} w-[38%] shrink-0 truncate font-mono text-[color:var(--muted-soft)]`}>
        "{oneLine(item.quote, 90)}"
      </span>
    </div>
  );
}

// A labelled block of the exact bytes the judge was given. The clip note is
// part of the block, not a footnote: on a long call the judge read a TRUNCATED
// view, and a reader who does not know that will debug a dispute against bytes
// the judge never had.
function ByteBlock({ label, text, cut = 0 }) {
  if (!text && !cut) return null;
  return (
    <div className="min-w-0">
      <div className={`${EYEBROW} flex items-baseline gap-[8px]`}>
        <span className="shrink-0">{label}</span>
        {cut > 0 ? (
          <span className={`${META} min-w-0 truncate normal-case tracking-normal text-[color:var(--muted-soft)]`}>
            {cut.toLocaleString()} more characters were cut before the judge saw it
          </span>
        ) : null}
      </div>
      <pre className="mt-[1px] max-h-[132px] overflow-auto whitespace-pre-wrap break-words rounded-[2px] bg-[color:var(--control-bg)] px-[6px] py-[4px] font-mono text-[11px] leading-[1.45] text-[color:var(--muted)]">
        {text || "(empty)"}
      </pre>
    </div>
  );
}

// One labelled group inside an opened call. The sections are what makes the
// expansion readable rather than a wall: what was claimed, what was missed,
// and what the judge was looking at when it decided.
function Section({ label, count = null, children }) {
  return (
    <div className="min-w-0 border-t border-[color:var(--line-soft)] pt-[4px]">
      <div className={`${EYEBROW} flex items-baseline gap-[8px] pb-[2px]`}>
        <span className="shrink-0">{label}</span>
        {count != null ? <span className="shrink-0 text-[color:var(--muted-soft)]">{count}</span> : null}
      </div>
      <div className="grid min-w-0 gap-[1px]">{children}</div>
    </div>
  );
}

// The account of the run, in words: what the session came to, and the steps it
// took to get there.
//
// It is written from the CORRECTED reading -- accepted claims and
// quote-anchored additions -- so it inherits those gates rather than narrating
// raw turns. Every literal it names is checked against those operations, and a
// line naming a file or endpoint the reading never saw is dropped before it
// reaches here. When lines were dropped the section SAYS so: a summary that
// quietly lost half its account would be read as the whole one.
// What the account has to admit about itself, in the order it matters: the
// stretch it was written from, then the lines the gate refused. Silence on
// either would let a partial, filtered account read as the whole one.
function summaryNote(summary) {
  if (!summary) return null;
  const notes = [];
  if (summary.opsShown < summary.ops) {
    notes.push(`written from the first ${summary.opsShown.toLocaleString()} of ${summary.ops.toLocaleString()} operations`);
  }
  if (summary.dropped) {
    notes.push(`${summary.dropped} line${summary.dropped === 1 ? "" : "s"} dropped \u2014 ${
      summary.dropped === 1 ? "it named" : "they named"
    } something the reading never saw`);
  }
  return notes.join(" \u00b7 ") || null;
}

function SummarySection({ state }) {
  if (!state) return null;
  const { status, summary, error } = state;
  return (
    <div data-reading-summary="" aria-busy={status === "running" ? "true" : "false"}>
      <SectionRow
        label="What this agent did"
        detail={status === "running"
          ? "writing the account\u2026"
          : status === "error"
            ? (error?.message || "the account could not be written")
            : summaryNote(summary)}
      />
      {status === "done" && summary ? (
        <div className="grid min-w-0 gap-[3px] px-[9px] py-[4px]">
          {summary.outcome ? (
            <div data-reading-summary-outcome="" className="min-w-0 text-[12px] leading-[1.45] text-[color:var(--ink)]">
              {summary.outcome}
            </div>
          ) : null}
          {summary.steps.map((step, i) => (
            <div key={i} data-reading-summary-step="" className={`${META} flex min-w-0 items-baseline gap-[8px]`}>
              <span className="w-[14px] shrink-0 text-right tabular-nums text-[color:var(--muted-soft)]">{i + 1}</span>
              <span className="min-w-0 flex-1 text-[color:var(--muted)]">{step}</span>
            </div>
          ))}
          {!summary.outcome && !summary.steps.length ? (
            <span className={`${META} text-[color:var(--muted-soft)]`}>
              every line named something the reading never saw, so none of them are shown
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// One call the judge finished reading. Collapsed it is a tally; opened it is
// the whole exchange -- every claim with its ruling and the judge's reason, the
// work it found missing, and the bytes it was reading from.
function LandedRow({ row, call }) {
  const [open, setOpen] = useState(false);
  const first = row.additions[0] || null;
  const canOpen = (row.claims?.length || 0) > 0 || row.additions.length > 0 || !!call;
  return (
    <div data-reading-landed={row.id} className="min-w-0 px-[9px]">
      <button
        type="button"
        data-reading-landed-toggle={row.id}
        onClick={() => canOpen && setOpen((v) => !v)}
        aria-expanded={open}
        disabled={!canOpen}
        className={`flex w-full min-w-0 items-baseline gap-[9px] py-[2px] text-left ${
          canOpen ? "cursor-pointer" : "cursor-default"
        }`}
      >
        <span className={`${EYEBROW} w-[104px] shrink-0 truncate`}>
          <span aria-hidden="true" className="inline-block w-[9px] text-[color:var(--muted-soft)]">
            {canOpen ? (open ? "\u2304" : "\u203a") : " "}
          </span>
          Turn {Number.isFinite(row.turn) ? row.turn + 1 : "?"}
        </span>
        <span className={`${META} w-[104px] shrink-0 truncate font-mono text-[color:var(--muted)]`}>{row.name || "call"}</span>
        <span className={`${META} w-[112px] shrink-0 tabular-nums`}>
          {row.accepted ? <span style={{ color: OK }}>{row.accepted} kept</span> : null}
          {row.accepted && row.rejected ? <span className="text-[color:var(--muted-soft)]"> · </span> : null}
          {row.rejected ? <span style={{ color: AMBER }}>{row.rejected} disputed</span> : null}
          {!row.accepted && !row.rejected ? <span className="text-[color:var(--muted-soft)]">no claims</span> : null}
        </span>
        <span className={`${META} min-w-0 flex-1 truncate`}>
          {first ? (
            <>
              <span style={{ color: OK }}>+{row.additions.length} </span>
              <span className="text-[color:var(--ink)]">{first.kind} </span>
              <span className="font-mono text-[color:var(--muted)]">{first.subject}</span>
              <span className="font-mono text-[color:var(--muted-soft)]"> · "{oneLine(first.quote, 60)}"</span>
            </>
          ) : (
            <span className="text-[color:var(--muted-soft)]">nothing missed</span>
          )}
        </span>
      </button>
      {open ? (
        <div
          data-reading-landed-body={row.id}
          className="ml-[4px] grid min-w-0 gap-[5px] border-l border-[color:var(--line-soft)] pb-[7px] pl-[10px] pt-[2px]"
        >
          <Section label="What the drafters claimed" count={row.claims?.length || 0}>
            {row.claims?.length
              ? row.claims.map((c) => <ClaimLine key={c.index} claim={c} />)
              : <span className={`${META} text-[color:var(--muted-soft)]`}>nothing — this call reached the judge unclaimed</span>}
          </Section>
          {row.additions.length ? (
            <Section label="What they missed" count={row.additions.length}>
              {row.additions.map((a, i) => <AdditionLine key={`m${i}`} item={a} />)}
            </Section>
          ) : null}
          {call ? (
            <Section label="What the judge was looking at">
              <div data-reading-judge-card="" className="grid min-w-0 gap-[4px]">
                <ByteBlock label="Call as issued" text={call.callText} cut={call.callTextClipped || 0} />
                <ByteBlock label="Raw arguments" text={call.callJson} cut={call.callJsonClipped || 0} />
                <ByteBlock label="What it returned" text={call.result} cut={call.resultClipped || 0} />
              </div>
            </Section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function readingLine(totals, inFlight, busy) {
  if (totals.calls) {
    return `${totals.calls} call${totals.calls === 1 ? "" : "s"} read · ${totals.accepted} claim${
      totals.accepted === 1 ? "" : "s"
    } kept · ${totals.rejected} disputed · ${totals.additions} addition${totals.additions === 1 ? "" : "s"}${
      totals.discarded ? ` · ${totals.discarded} unanchored discarded` : ""
    }`;
  }
  if (!busy) return "nothing was read";
  return inFlight
    ? `${inFlight} judge${inFlight === 1 ? "" : "s"} reading — no verdicts yet`
    : "starting…";
}

function ReadingDock({
  rows = [],
  // The calls as DRAFTED, by id: the bytes and claims the judge was handed.
  // Kept apart from the rows because a row is a verdict and this is the
  // question it answered.
  callsById = null,
  // The account of the run: {status, summary, error}, or null before there is
  // a finished reading to describe.
  summary = null,
  totals,
  batches = [],
  busy = false,
  progress = null,
  onStop = null,
  onClose = null,
  // Start the reading. It lives HERE rather than in the toolbar because this
  // panel is the reader's whole surface for the trace: opening it costs
  // nothing, and spending a judge run is a decision they make from inside it,
  // next to the account it produces.
  onRead = null,
  fill = false,
  height = null,
  // Why the judge cannot run, said where the run button would be.
  unreadable = null,
  // A tab of the permanent Lanes dock: the dock owns the frame, the height
  // and the tab label, so this renders only the account.
  bare = false,
  // Puts the trace in the chat, to ask about it and its reading there.
  onAddToChat = null,
}) {
  // Nothing to say and nothing to offer: the caller has not opened this panel.
  if (!totals && !bare && !busy && !onRead && !unreadable) return null;
  const heads = [...batches].sort((a, b) => a.index - b.index);
  const inFlight = heads.filter((b) => b.status === "reading").length;
  // Whether this panel has a reading to make room for, which is NOT the same
  // as having totals: a run that has just started has no live totals for its
  // first frames, and collapsing the account's half for those frames made the
  // panel jump the moment the reader asked for a reading.
  const showsReading = !!totals || busy;
  const addToChat = onAddToChat ? (
    <button
      type="button"
      data-reading-dock-add-to-chat=""
      onClick={onAddToChat}
      className={`${META} inline-flex h-[20px] shrink-0 cursor-pointer items-center rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--control-bg)] px-[7px] text-[color:var(--ink)] hover:border-[color:var(--ink)]`}
    >
      Add to chat
    </button>
  ) : null;

  return (
    <div
      data-reading-dock=""
      aria-busy={busy ? "true" : "false"}
      aria-label="Reading this trace"
      className={bare
        ? "flex min-h-0 min-w-0 flex-1 flex-col"
        : "flex min-h-0 min-w-0 flex-none flex-col rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)]"}
      // The dock slot's own sizing contract, verbatim from LanesDock: the
      // reader's divider drag pins the height, and until then it takes up to
      // 46% of the column. Without it this dock ignored the divider entirely,
      // which is not what "one more detail expansion" means.
      style={fill && !bare ? (height != null ? { height } : { maxHeight: "46%" }) : undefined}
    >
      {/* As a tab, the row exists only to carry the run's totals and its
          stop: the tab already says "Reading", and a header saying it again
          over an offer row was a line of nothing. */}
      {bare && !totals && !busy ? null : (
      <div className="flex min-w-0 flex-none flex-wrap items-center gap-x-[10px] gap-y-[4px] border-b border-[color:var(--line-soft)] px-[9px] py-[5px]">
        {bare ? null : <span className={`${EYEBROW_BASE} shrink-0 font-medium text-[color:var(--eyebrow)]`}>Reading</span>}
        <span className={`${META} min-w-0 flex-1 truncate text-[color:var(--muted)] tabular-nums`}>
          {/* Empty while the offer row below is up: that row states the state
              AND what to do about it, and saying it twice was the redundancy
              this panel keeps accumulating. */}
          {totals ? readingLine(totals, inFlight, busy) : busy ? "starting…" : null}
        </span>
        {/* Banked verdicts answer before the run starts and cost nothing. The
            dock says so beside the totals, so a mostly-reused run is never
            mistaken for a mostly-worked one. */}
        {totals?.reused ? (
          <span className={`${META} shrink-0 text-[color:var(--muted-soft)] tabular-nums`}>
            {totals.reused} reused, unspent
          </span>
        ) : null}
        {progress?.total > 1 ? (
          <span className={`${META} shrink-0 text-[color:var(--muted-soft)] tabular-nums`}>
            batch {Math.min(progress.done + 1, progress.total)}/{progress.total}
          </span>
        ) : null}
        {showsReading ? addToChat : null}
        {busy && onStop ? (
          <button
            type="button"
            data-reading-dock-stop=""
            onClick={onStop}
            className={`${META} shrink-0 cursor-pointer p-0 text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]`}
          >
            stop
          </button>
        ) : null}
        {onClose ? (
          <button
            type="button"
            data-reading-dock-close=""
            onClick={onClose}
            aria-label="Close the reading"
            className="shrink-0 cursor-pointer text-[12px] leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)]"
          >
            ×
          </button>
        ) : null}
      </div>
      )}

      {/* With an account to show, the account scrolls in the column; with
          NO reading run there is only the offer, at its own height. */}
      <div data-reading-dock-body="" className={`min-w-0 overflow-y-auto ${fill ? (showsReading ? "min-h-0 flex-1" : "flex-none") : "max-h-[42vh]"}`}>
        {/* The offer, on its own line and next to the sentence that explains
            why it is there. Tucked at the far end of the header row beside the
            close, it was easy to miss entirely -- which for the one action
            this state exists to invite is the whole cost of the state. */}
        {!showsReading ? (
          <div
            data-reading-offer=""
            className="flex min-w-0 flex-wrap items-center gap-x-[12px] gap-y-[6px] border-b border-[color:var(--line-soft)] px-[9px] py-[8px]"
          >
            <span className={`${META} min-w-0 flex-1 text-[color:var(--muted)]`}>
              {onRead || !unreadable
                ? "Nothing has judged this trace's automated reading yet."
                : unreadable}
            </span>
            {onRead ? (
              <button
                type="button"
                data-reading-dock-read=""
                onClick={onRead}
                className={`${META} shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--eyebrow)] bg-[color:var(--control-bg)] px-[8px] py-[3px] font-medium leading-none text-[color:var(--eyebrow)] hover:bg-[color:var(--eyebrow)] hover:text-[color:var(--plot-bg)]`}
              >
                Read this trace
              </button>
            ) : null}
            {addToChat}
          </div>
        ) : null}
        {/* First, because it is the only part a reader who wants one sentence
            can stop at. Everything below it is the evidence for it. */}
        <SummarySection state={summary} />
        {heads.length ? (
          <>
            <SectionRow
              label="Judges"
              detail={inFlight
                ? `${inFlight} of ${heads.length} still reading`
                : `${heads.length} batch${heads.length === 1 ? "" : "es"}, all finished`}
            />
            <div className="py-[2px]">
              {heads.map((b) => <HeadRow key={b.index} batch={b} />)}
            </div>
          </>
        ) : null}
        {totals ? (
          <SectionRow
            label={busy ? "Read so far" : "Read"}
            detail={rows.length
              ? `${rows.length} call${rows.length === 1 ? "" : "s"}, newest first`
              : "verdicts land here as the judge writes them"}
          />
        ) : null}
        {rows.length ? (
          <div className="py-[2px]">
            {rows.map((row) => <LandedRow key={row.id} row={row} call={callsById?.get(row.id) || null} />)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default memo(ReadingDock);
